import type { ReactNode } from "react";
import moment from "moment-timezone";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CommonDestinationTimezone } from "@/lib/utils";
import { TaskInstance } from "@/lib/api/checklist";
import { StatusIndicator } from "../components";
import { useStytchMember } from "@stytch/react/b2b";
import { getUserDbId } from "@/lib/models/auth";
import { TaskInstanceActionContext } from "./taskInstanceActions/types";
import { getTaskInstanceAction } from "./taskInstanceActions/registry";

interface TaskInstanceActionModalProps {
  open: boolean;
  onClose: () => void;
  taskInstance: TaskInstance;
  templateName?: string;
}

export function TaskInstanceActionModal({
  open,
  onClose,
  taskInstance,
  templateName,
}: TaskInstanceActionModalProps) {
  const { member } = useStytchMember();
  const currentUserId = member ? getUserDbId(member) : null;

  const taskTitle = taskInstance.task?.title || "Task Instance";
  const derivedTemplateName =
    templateName || taskInstance.template_instance?.template?.name || "Checklist Template";

  const actionContext: TaskInstanceActionContext = {
    taskInstance,
    taskTitle,
    templateName: derivedTemplateName,
  };

  const matchedAction = getTaskInstanceAction(actionContext);
  const ActionComponent = matchedAction?.Component;

  const formatDate = (date?: string) => {
    if (!date) return "—";
    return moment.tz(date, CommonDestinationTimezone).format("MMM DD, YYYY h:mm A");
  };

  const triggerMetaData = taskInstance.template_instance?.trigger_meta_data || {};
  const customerName = triggerMetaData.customer_name;
  const customerEmail = triggerMetaData.email;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-[90vw] min-w-[90vw] min-h-[90vh] max-h-[95vh] flex flex-col">
        <DialogHeader className="space-y-1 flex flex-col">
          <DialogTitle className="text-2xl font-semibold">{taskTitle}</DialogTitle>
          <DialogDescription className="text-base">
            Task instance actions for template: <span className="font-medium">{derivedTemplateName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2 h-full">
          <div className="rounded-xl border border-border bg-muted/30 p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <SummaryItem label="Template" value={derivedTemplateName} />
              <SummaryItem label="Assigned To" value={taskInstance.assigned_to?.name || "Unassigned"} />
              <SummaryItem label="Due Date" value={formatDate(taskInstance.due_date)} />
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
                <StatusIndicator status={taskInstance.status} secondary_status={taskInstance.secondary_status} />
              </div>
              {customerName && <SummaryItem label="Customer Name" value={customerName} />}
              {customerEmail && <SummaryItem label="Customer Email" value={customerEmail} />}
            </div>
          </div>

          {ActionComponent ? (
            <ActionComponent
              {...actionContext}
              onClose={onClose}
              currentUserId={currentUserId}
            />
          ) : (
            <NoActionFallback />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground">{value || "—"}</p>
    </div>
  );
}

function NoActionFallback() {
  return (
    <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
      No contextual actions are configured for this task yet.
      <br />
      Please reach out to the automations team to add workflow-specific actions.
    </div>
  );
}

